import { type NegotiationEntity, NegotiationOutcome, NegotiationState } from '~/apiClient';

export enum NegotiationUiStatus {
    Successful = 'Successful',
    InProgress = 'InProgress',
    Unsuccessful = 'Unsuccessful',
}

export type ExtendedNegotiation<T> = T & {
    isBuyer: boolean;
    isEnded: boolean;
    isAccepted: boolean;
    isExpired: boolean;
    uiStatus: NegotiationUiStatus;
    isSuccessful: boolean;
    isInProgress: boolean;
    isUnsuccessful: boolean;
};

type UseExtendedNegotiationReturn = {
    extendNegotiation: <T extends Pick<NegotiationEntity, 'state' | 'outcome' | 'buyerCompanyId'>>(negotiation: T) => ExtendedNegotiation<T>;
};

export const useExtendedNegotiation = (): UseExtendedNegotiationReturn => {
    const { data: authData } = useAuthUtils();

    const extendNegotiation: UseExtendedNegotiationReturn['extendNegotiation'] = negotiation => {
        const isBuyer = negotiation.buyerCompanyId === authData.value?.user?.companyId;

        const isAccepted =
            !!negotiation.outcome && [NegotiationOutcome.AcceptedByBuyer, NegotiationOutcome.AcceptedBySeller].includes(negotiation.outcome);

        const isEnded = [NegotiationState.Closed, NegotiationState.Unpaid].includes(negotiation.state);

        const isExpired = negotiation.outcome === NegotiationOutcome.Expired;

        const uiStatus = ((): NegotiationUiStatus => {
            if (isAccepted && negotiation.state !== NegotiationState.Unpaid) return NegotiationUiStatus.Successful;
            if (negotiation.state === NegotiationState.Open) return NegotiationUiStatus.InProgress;

            return NegotiationUiStatus.Unsuccessful;
        })();

        return {
            isBuyer,
            isEnded,
            isAccepted,
            isExpired,
            uiStatus,
            isSuccessful: uiStatus === NegotiationUiStatus.Successful,
            isInProgress: uiStatus === NegotiationUiStatus.InProgress,
            isUnsuccessful: uiStatus === NegotiationUiStatus.Unsuccessful,
            ...negotiation,
        };
    };

    return {
        extendNegotiation,
    };
};
